import {
  AllocationWithAmount,
  CheckPaymentDetails,
} from '../payment-method-selection-dialog';
import { bucketFillCheckAllocations } from './bucket-fill-check-allocations';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Same calendar day in subsequent months (handles month length). */
export function addMonthsToDate(date: Date, monthsToAdd: number): Date {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + monthsToAdd);
  return d;
}

/** e.g. "22333474" → "22333475"; non-numeric falls back to `base-2`, `base-3`. */
export function referenceIdForSeriesIndex(
  referenceIdStart: string,
  index: number,
): string {
  const base = referenceIdStart.trim();
  if (/^\d+$/.test(base)) {
    try {
      return (BigInt(base) + BigInt(index)).toString();
    } catch {
      return base;
    }
  }
  return index === 0 ? base : `${base}-${index + 1}`;
}

export function generateSerialChecks(params: {
  allocationAmounts: AllocationWithAmount[];
  existingChecks: CheckPaymentDetails[];
  bankNumber: string;
  branchNumber: string;
  accountNumber: string;
  startDueDate: Date;
  amountPerCheck: number;
  referenceIdStart: string;
  numberOfChecks: number;
}): CheckPaymentDetails[] {
  const {
    allocationAmounts,
    existingChecks,
    bankNumber,
    branchNumber,
    accountNumber,
    startDueDate,
    amountPerCheck,
    referenceIdStart,
    numberOfChecks,
  } = params;

  const alreadyAllocatedByAllocationId: Record<number, number> = {};

  for (const chk of existingChecks) {
    for (const a of chk.allocations ?? []) {
      alreadyAllocatedByAllocationId[a.allocationId] = round2(
        (alreadyAllocatedByAllocationId[a.allocationId] || 0) + (a.amount || 0),
      );
    }
  }

  const bn = bankNumber.trim();
  const br = branchNumber.trim();
  const ac = accountNumber.trim();
  const amt = round2(amountPerCheck);

  const result: CheckPaymentDetails[] = [];

  for (let i = 0; i < numberOfChecks; i++) {
    const allocations = bucketFillCheckAllocations({
      allocationTotals: allocationAmounts,
      checkAmount: amt,
      alreadyAllocatedByAllocationId,
    });

    for (const a of allocations) {
      alreadyAllocatedByAllocationId[a.allocationId] = round2(
        (alreadyAllocatedByAllocationId[a.allocationId] || 0) + a.amount,
      );
    }

    result.push({
      referenceId: referenceIdForSeriesIndex(referenceIdStart, i),
      amount: amt,
      bankNumber: bn,
      branchNumber: br,
      accountNumber: ac,
      dueDate: addMonthsToDate(startDueDate, i),
      allocations,
    });
  }

  return result;
}
